/**
 * Typed fetch wrapper for the Tolkgram REST API.
 *
 * Conventions:
 *   - All requests include `Authorization: Bearer <jwt>` once we have one.
 *   - Bodies are JSON (no FormData yet).
 *   - On 401, we fire an `auth:invalid` window event so `AuthProvider` can
 *     clear state and bounce the user back to /connect.
 *   - We throw `ApiError` with status + parsed body for non-2xx; callers
 *     decide whether to surface or swallow.
 *
 * The functions below are intentionally one-shot — react-query handles
 * caching, retry, dedupe.
 */
import { env } from "./env";
import { getInitData } from "./telegram";
import type {
  ComposePrepareRequest,
  ComposePrepareResponse,
  Contact,
  InboxResponse,
  MeResponse,
  ProofNonceResponse,
  ReadAck,
  ThreadResponse,
  WalletAuthResponse,
} from "../types/api";

export class ApiError extends Error {
  readonly status: number;
  readonly body: unknown;
  constructor(status: number, message: string, body: unknown) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.body = body;
  }
}

let cachedJwt: string | null = null;
let cachedTgSession: string | null = null;

/** Called by `AuthProvider` when the JWT changes. */
export function setApiJwt(jwt: string | null): void {
  cachedJwt = jwt;
}

export function getApiJwt(): string | null {
  return cachedJwt;
}

/**
 * tg_session is captured from /auth/telegram (or /auth/dev-session) and sent
 * as `x-tg-session` on subsequent auth-flow requests. The header path is
 * needed because Telegram's webview blocks third-party cookies between the
 * Pages origin and the Workers origin.
 */
export function setApiTgSession(token: string | null): void {
  cachedTgSession = token;
}

interface RequestOptions {
  method?: "GET" | "POST" | "PUT" | "DELETE";
  body?: unknown;
  signal?: AbortSignal;
  /** Skip the Authorization header (for /auth/telegram pre-JWT). */
  noAuth?: boolean;
  /** Override credentials mode (for /auth/telegram which sets the tg_session cookie). */
  credentials?: RequestCredentials;
}

async function request<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const url = `${env.apiUrl}${path.startsWith("/") ? path : `/${path}`}`;
  const headers: Record<string, string> = {
    Accept: "application/json",
  };
  if (opts.body !== undefined) headers["Content-Type"] = "application/json";
  if (!opts.noAuth && cachedJwt) headers["Authorization"] = `Bearer ${cachedJwt}`;
  if (cachedTgSession) headers["x-tg-session"] = cachedTgSession;

  let res: Response;
  try {
    res = await fetch(url, {
      method: opts.method ?? "GET",
      headers,
      body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
      signal: opts.signal,
      credentials: opts.credentials ?? "include",
    });
  } catch (err) {
    // Network error / aborted.
    if (err instanceof Error && err.name === "AbortError") throw err;
    throw new ApiError(0, "Network error", err);
  }

  let parsed: unknown = null;
  const text = await res.text();
  if (text.length > 0) {
    try {
      parsed = JSON.parse(text);
    } catch {
      parsed = text;
    }
  }

  if (!res.ok) {
    if (res.status === 401) {
      // Let AuthProvider clear and bounce.
      try {
        window.dispatchEvent(new CustomEvent("auth:invalid"));
      } catch {
        /* not in a browser? */
      }
    }
    const message =
      parsed && typeof parsed === "object" && parsed !== null && "error" in parsed
        ? String((parsed as { error: unknown }).error)
        : `HTTP ${res.status}`;
    throw new ApiError(res.status, message, parsed);
  }

  return parsed as T;
}

/* ------------------------------- Auth ------------------------------------ */

interface TgSessionResponse {
  tg_session: string;
  tg_user_id: number;
  tg_username: string | null;
}

export async function authTelegram(): Promise<TgSessionResponse> {
  const initData = getInitData();
  if (initData.length === 0) {
    throw new ApiError(0, "Not running in Telegram (no initData)", null);
  }
  const res = await request<TgSessionResponse>("/auth/telegram", {
    method: "POST",
    body: { init_data: initData },
    noAuth: true,
  });
  // Capture for the header fallback — Telegram webview blocks the cookie.
  setApiTgSession(res.tg_session);
  return res;
}

export async function authProofNonce(): Promise<ProofNonceResponse> {
  return request<ProofNonceResponse>("/auth/proof-nonce", { noAuth: true });
}

export async function authDevSession(): Promise<{ tg_session: string }> {
  const res = await request<{ tg_session: string }>("/auth/dev-session", {
    method: "POST",
    noAuth: true,
  });
  setApiTgSession(res.tg_session);
  return res;
}

export async function authWallet(payload: {
  address: string;
  network: string;
  public_key: string;
  proof: {
    timestamp: number;
    domain: { length_bytes: number; value: string };
    payload: string;
    signature: string;
  };
}): Promise<WalletAuthResponse> {
  return request<WalletAuthResponse>("/auth/wallet", {
    method: "POST",
    body: payload,
    noAuth: true,
  });
}

/* --------------------------------- Me ------------------------------------ */

export async function getMe(signal?: AbortSignal): Promise<MeResponse> {
  return request<MeResponse>("/me", { signal });
}

/* -------------------------------- Inbox ---------------------------------- */

export async function getInbox(
  params: { cursor?: string; limit?: number } = {},
  signal?: AbortSignal,
): Promise<InboxResponse> {
  const qs = new URLSearchParams();
  if (params.cursor !== undefined) qs.set("cursor", params.cursor);
  if (params.limit !== undefined) qs.set("limit", String(params.limit));
  const suffix = qs.toString().length > 0 ? `?${qs.toString()}` : "";
  return request<InboxResponse>(`/inbox${suffix}`, { signal });
}

/* -------------------------------- Threads -------------------------------- */

export async function getThread(
  counterparty: string,
  params: { before_lt?: string; limit?: number } = {},
  signal?: AbortSignal,
): Promise<ThreadResponse> {
  const qs = new URLSearchParams();
  if (params.before_lt !== undefined) qs.set("before_lt", params.before_lt);
  if (params.limit !== undefined) qs.set("limit", String(params.limit));
  const suffix = qs.toString().length > 0 ? `?${qs.toString()}` : "";
  return request<ThreadResponse>(
    `/threads/${encodeURIComponent(counterparty)}${suffix}`,
    { signal },
  );
}

export async function markThreadRead(
  counterparty: string,
  lastReadLt: string,
): Promise<ReadAck> {
  return request<ReadAck>(`/threads/${encodeURIComponent(counterparty)}/read`, {
    method: "POST",
    body: { last_read_lt: lastReadLt },
  });
}

/* -------------------------------- Compose -------------------------------- */

export async function composePrepare(
  body: ComposePrepareRequest,
): Promise<ComposePrepareResponse> {
  return request<ComposePrepareResponse>("/compose/prepare", {
    method: "POST",
    body,
  });
}

/**
 * Best-effort cancellation of a pending compose row. Backend Architect may not
 * have shipped this endpoint yet — if we get a 404 we treat it as benign
 * (the row will expire in 10 min server-side anyway, per Section 6).
 */
export async function composeCancel(pendingId: string): Promise<void> {
  try {
    await request<{ ok: true }>(`/compose/pending/${encodeURIComponent(pendingId)}`, {
      method: "DELETE",
    });
  } catch (err) {
    if (err instanceof ApiError && (err.status === 404 || err.status === 0)) {
      // Endpoint may not exist yet; pending row will expire server-side.
      return;
    }
    throw err;
  }
}

/* -------------------------------- Contacts ------------------------------- */

export async function getContacts(): Promise<Contact[]> {
  return request<Contact[]>("/contacts");
}

export async function upsertContact(
  wallet: string,
  body: { nickname?: string; muted?: boolean },
): Promise<Contact> {
  return request<Contact>(`/contacts/${encodeURIComponent(wallet)}`, {
    method: "PUT",
    body,
  });
}

export async function deleteContact(wallet: string): Promise<void> {
  await request<{ ok: true }>(`/contacts/${encodeURIComponent(wallet)}`, {
    method: "DELETE",
  });
}
